import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { LevelBadge, MannerBadge } from "./Badges";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";
import ReportModal from "./components/ReportModal";
import pickleballIcon from "./pickleball.png";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const SPORTS_LABEL = {
  tennis: "테니스", soccer: "축구", futsal: "풋살", badminton: "배드민턴",
  basketball: "농구", bowling: "볼링", table_tennis: "탁구", baseball: "야구",
  golf: "골프", pickleball: "피클볼", billiards: "당구",
};
const SPORTS_ICON = {
  tennis: "🎾", soccer: "⚽", futsal: "🥅", badminton: "🏸",
  basketball: "🏀", bowling: "🎳", table_tennis: "🏓", baseball: "⚾",
  golf: "⛳", pickleball: "🏸", billiards: "🎱",
};

function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

// teams.sports_type 값을 user_sports.sports_type 형식으로 변환
function normalizeSportsType(type) {
  if (type === "tabletennis") return "table_tennis";
  if (type === "running")     return "pickleball";
  return type || "";
}

function formatSportDetail(rawType, cd) {
  if (!cd) return null;
  const t = normalizeSportsType(rawType);
  switch (t) {
    case "tennis":       return cd.ntrp ? `NTRP ${cd.ntrp}` : null;
    case "soccer":
    case "futsal":       return cd.position || null;
    case "badminton":    return cd.level || null;
    case "basketball":   return [cd.position, cd.level].filter(Boolean).join(" · ") || null;
    case "bowling":      return cd.avg ? `AVG ${cd.avg}` : null;
    case "table_tennis": return cd.level || null;
    case "baseball":     return cd.position || null;
    case "golf":         return cd.handicap ? `핸디캡 ${cd.handicap}` : null;
    case "pickleball":
    case "billiards":    return cd.years ? `구력 ${cd.years}년` : null;
    default:             return null;
  }
}

export default function TeamDetail({ teamId, onBack, onMatchesClick }) {
  const { user, activeTeam } = useAuth();
  const currentUserId = user?.id || null;

  const [team, setTeam]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [myMember, setMyMember]       = useState(undefined);
  const [applyLoading, setApplyLoading] = useState(false);
  const [teamMembers, setTeamMembers]   = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [viewerSrc, setViewerSrc]     = useState(null);
  const [showReport, setShowReport]   = useState(false);
  const [blockDone, setBlockDone]     = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalProfile, setModalProfile]     = useState(null);
  const [modalSports, setModalSports]       = useState([]);
  const [modalLoading, setModalLoading]     = useState(false);

  const isOwner       = team && currentUserId && team.user_id === currentUserId;
  const isLeader      = !!isOwner;
  // 이미 수락된 팀원이면 교류전 신청 버튼 숨김
  const isMemberOfTeam = myMember?.status === "accepted";

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("teams")
      .select("*")
      .eq("id", teamId)
      .maybeSingle();
    setTeam(data);
    setLoading(false);
  }, [teamId]);

  const fetchMyMember = useCallback(async () => {
    if (!currentUserId || !teamId) { setMyMember(null); return; }
    const { data } = await supabase
      .from("team_members")
      .select("id, status, role")
      .eq("team_id", teamId)
      .eq("user_id", currentUserId)
      .maybeSingle();
    setMyMember(data || null);
  }, [teamId, currentUserId]);

  useEffect(() => {
    fetchTeam();
    fetchMyMember();
  }, [fetchTeam, fetchMyMember]);

  // 팀원 클릭 시 전체 프로필 + 해당 팀 종목만 로드
  useEffect(() => {
    if (!selectedMember) return;
    const fetch = async () => {
      setModalLoading(true);
      const normalType = normalizeSportsType(team?.sports_type || "");
      const [{ data: fp }, { data: sp }] = await Promise.all([
        supabase.from("user_profiles").select("*").eq("id", selectedMember.user_id).maybeSingle(),
        supabase.from("user_sports").select("*").eq("user_id", selectedMember.user_id).eq("sports_type", normalType),
      ]);
      setModalProfile(fp || selectedMember.profile || null);
      setModalSports(sp || []);
      setModalLoading(false);
    };
    fetch();
  }, [selectedMember]); // eslint-disable-line react-hooks/exhaustive-deps

  // accepted 팀원 목록 + 프로필 + 종목 정보 로드
  useEffect(() => {
    if (!teamId || !team?.sports_type) return;
    const load = async () => {
      setMembersLoading(true);
      const { data: rows } = await supabase
        .from("team_members")
        .select("id, user_id, nickname")
        .eq("team_id", teamId)
        .eq("status", "accepted");

      if (!rows || rows.length === 0) { setTeamMembers([]); setMembersLoading(false); return; }

      const userIds      = rows.map(r => r.user_id);
      const normalType   = normalizeSportsType(team.sports_type);
      const [{ data: profiles }, { data: sports }] = await Promise.all([
        supabase.from("user_profiles").select("id, nickname, profile_image, gender").in("id", userIds),
        supabase.from("user_sports").select("user_id, career_detail, is_player").in("user_id", userIds).eq("sports_type", normalType),
      ]);
      const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
      const sportMap   = Object.fromEntries((sports   || []).map(s => [s.user_id, s]));
      setTeamMembers(rows.map(r => ({
        id: r.id, user_id: r.user_id,
        team_nick: r.nickname || null,
        profile: profileMap[r.user_id] || null,
        sport:   sportMap[r.user_id]   || null,
      })));
      setMembersLoading(false);
    };
    load();
  }, [teamId, team?.sports_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlock = async () => {
    if (!activeTeam?.id || !team?.id) return;
    setBlockLoading(true);
    await supabase.from("blocks").upsert({
      blocker_team_id: activeTeam.id,
      blocked_team_id: team.id,
    }, { onConflict: "blocker_team_id,blocked_team_id" });
    setBlockLoading(false);
    setBlockDone(true);
    setTimeout(onBack, 1200);
  };

  const handleApply = async () => {
    if (!currentUserId) return;
    setApplyLoading(true);
    const { data: existing } = await supabase
      .from("team_members").select("id")
      .eq("team_id", teamId).eq("user_id", currentUserId)
      .maybeSingle();
    if (existing) {
      setApplyLoading(false);
      alert("이미 신청했거나 가입된 팀이에요");
      fetchMyMember();
      return;
    }
    await supabase.from("team_members").insert({
      team_id: teamId,
      user_id: currentUserId,
      role: "member",
      status: "pending",
    });
    setApplyLoading(false);
    fetchMyMember();
  };

  if (loading) {
    return (
      <div style={s.container}>
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
          <p style={{ color: "#9ca3af", fontSize: 14, marginTop: 16 }}>불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div style={s.container}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={onBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            뒤로
          </button>
        </div>
        <div style={s.loadingWrap}>
          <p style={{ fontSize: 15, color: "#6b7280" }}>팀 정보를 찾을 수 없어요.</p>
        </div>
      </div>
    );
  }

  const sport = SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type || "-" };
  const totalMembers = (team.male_count || 0) + (team.female_count || 0);
  const availDays  = parseArray(team.available_days);
  const availTimes = parseArray(team.available_times);

  return (
    <div style={s.container}>
      {/* 헤더 */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          뒤로
        </button>
      </div>

      <div style={s.body}>
        {/* 히어로 카드 */}
        <div style={s.card}>
          <div style={{ textAlign: "center", marginBottom: 14 }}>
            {team.logo_url ? (
              <img src={team.logo_url} alt="팀 로고" loading="lazy"
                style={{ width: 64, height: 64, borderRadius: 14, objectFit: "cover", border: "2px solid #e5e7eb", marginBottom: 8, cursor: "pointer" }}
                onClick={() => setViewerSrc(team.logo_url)}
              />
            ) : (
              <div style={{ fontSize: 38, marginBottom: 6 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 38, height: 38, objectFit: "contain" }} />}</div>
            )}
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{team.team_name}</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>{sport.label}</div>
            <div style={{ display: "flex", gap: 5, justifyContent: "center", flexWrap: "wrap" }}>
              {team.level && (
                <span style={{ padding: "3px 10px", background: "#fff5f5", border: "1px solid #E24B4A", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#E24B4A" }}>
                  {team.level}
                </span>
              )}
              {team.skill_level && (
                <span style={{ padding: "3px 10px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0369a1" }}>
                  {team.skill_level}
                </span>
              )}
            </div>
            {(team.match_level != null || team.manner_avg != null) && (
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
                {team.match_level != null && <LevelBadge level={team.match_level} />}
                {team.manner_avg != null && <MannerBadge score={team.manner_avg} />}
              </div>
            )}
            {team.region && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>📍 서울 {team.region}</div>}
          </div>

          {/* 단체사진 */}
          {team.team_photo && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>📸 팀 단체사진</div>
              <img src={team.team_photo} alt="단체사진" loading="lazy"
                style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb", cursor: "pointer", display: "block" }}
                onClick={() => setViewerSrc(team.team_photo)}
              />
            </div>
          )}

          {/* 팀 정보 */}
          <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "2px 12px", marginBottom: 10 }}>
            <InfoRow label="구력 레벨" value={formatSportLevel(team.sports_type, team.level)} />
            {team.skill_level && <InfoRow label="실력" value={team.skill_level} />}
            <InfoRow label="전적 레벨" value={formatRecordLevel(team)} />
            <InfoRow label="전적" value={formatRecord(team)} />
            <InfoRow label="매너 점수" value={formatManner(team)} />
            <InfoRow label="총 인원" value={`${totalMembers}명 (남 ${team.male_count || 0} / 여 ${team.female_count || 0})`} />
            <InfoRow label="연령대" value={parseAgeGroup(team.age_group) || "-"} />
            <InfoRow label="평균 구력" value={team.member_avg_career || "-"} />
            {team.main_place && <InfoRow label="주 활동 장소" value={team.main_place} />}
            {availDays.length > 0 && <InfoRow label="매칭 가능 요일" value={availDays.join(", ")} />}
            {availTimes.length > 0 && <InfoRow label="매칭 가능 시간" value={availTimes.join(", ")} />}
            {team.parking != null && (
              <InfoRow label="주차" value={team.parking ? `가능${team.parking_count ? ` (${team.parking_count}대)` : ""}` : "불가능"} />
            )}
          </div>

          {/* 팀 소개 */}
          {team.intro && (
            <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>팀 소개</div>
              <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{team.intro}</div>
            </div>
          )}

          {/* 팀원 가입 신청 - 카드 우측 하단 */}
          {!isOwner && currentUserId && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
              {myMember === undefined ? null : myMember === null ? (
                <button
                  style={{ ...s.smallApplyBtn, opacity: applyLoading ? 0.5 : 1 }}
                  onClick={handleApply}
                  disabled={applyLoading}
                >
                  {applyLoading ? "신청 중..." : "팀원 가입 신청"}
                </button>
              ) : myMember.status === "pending" ? (
                <span style={s.smallPendingText}>신청 완료 (대기중)</span>
              ) : null}
            </div>
          )}
        </div>

        {/* 교류전 신청 버튼 — 팀 소속(팀원/팀장)이 아닌 경우만 */}
        {!isOwner && currentUserId && (
          <div style={s.btnGroup}>
            {!isMemberOfTeam && (
              <button
                style={s.outlineBtn}
                onClick={() => onMatchesClick?.(teamId)}
              >
                교류전 신청
              </button>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                style={s.dangerOutlineBtn}
                onClick={() => setShowReport(true)}
              >
                🚨 신고하기
              </button>
              <button
                style={{ ...s.dangerOutlineBtn, opacity: blockLoading ? 0.5 : 1 }}
                onClick={handleBlock}
                disabled={blockLoading || blockDone}
              >
                {blockDone ? "✅ 차단됨" : blockLoading ? "차단 중..." : "🚫 차단하기"}
              </button>
            </div>
          </div>
        )}

        {/* 팀원 목록 */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 10 }}>
            팀원 ({teamMembers.length}명)
          </div>
          {membersLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <div style={s.spinner} />
            </div>
          ) : teamMembers.length === 0 ? (
            <p style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "16px 0" }}>
              아직 팀원이 없어요
            </p>
          ) : (
            teamMembers.map(m => (
              <MemberCard key={m.id} member={m} sportsType={team.sports_type} onClick={() => setSelectedMember(m)} />
            ))
          )}
        </div>
      </div>

      {selectedMember && (
        <MemberProfileModal
          member={selectedMember}
          profile={modalProfile}
          sports={modalSports}
          loading={modalLoading}
          onClose={() => setSelectedMember(null)}
        />
      )}

      {showReport && team && (
        <ReportModal
          targetType="team"
          targetId={team.id}
          targetName={team.team_name}
          onClose={() => setShowReport(false)}
        />
      )}

      {/* 이미지 뷰어 */}
      {viewerSrc && (
        <div style={s.viewerBackdrop} onClick={() => setViewerSrc(null)}>
          <img src={viewerSrc} alt="확대" style={s.viewerImg} />
        </div>
      )}
    </div>
  );
}

function MemberCard({ member, sportsType, onClick }) {
  const { profile, sport } = member;
  const nick     = profile?.nickname || member.team_nick || "(닉네임 없음)";
  const initials = nick[0]?.toUpperCase() || "?";
  const genderLabel = profile?.gender === "male" ? "남" : profile?.gender === "female" ? "여" : null;
  const isPlayerLabel = sport ? (sport.is_player ? "선출" : "비선출") : null;
  const level    = formatSportDetail(sportsType, sport?.career_detail);
  const detail   = [level, isPlayerLabel, genderLabel].filter(Boolean).join(" · ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#fff", borderRadius: 10, marginBottom: 8, border: "1px solid #f3f4f6", cursor: "pointer" }} onClick={onClick}>
      {profile?.profile_image ? (
        <img src={profile.profile_image} alt="profile"
          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#E24B4A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{initials}</span>
        </div>
      )}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{nick}</div>
        {detail && <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

function ModalInfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

function MemberProfileModal({ member, profile, sports, loading, onClose }) {
  const nick       = profile?.nickname || member.team_nick || "(닉네임 없음)";
  const initials   = nick[0]?.toUpperCase() || "?";
  const genderChip = profile?.gender === "male" ? "남성" : profile?.gender === "female" ? "여성" : null;
  const genderFull = profile?.gender === "male" ? "남자" : profile?.gender === "female" ? "여자" : null;
  const sp         = sports[0] || null;
  const spLabel    = sp ? (SPORTS_LABEL[sp.sports_type] || sp.sports_type) : null;
  const spIcon     = sp ? (SPORTS_ICON[sp.sports_type] || "🏅") : null;
  const spLevel    = sp ? formatSportDetail(sp.sports_type, sp.career_detail) : null;

  return (
    <div style={ms.backdrop} onClick={onClose}>
      <div style={ms.sheet} onClick={e => e.stopPropagation()}>
        <button style={ms.closeBtn} onClick={onClose}>✕</button>

        {/* 상단 프로필 */}
        <div style={ms.profileTop}>
          {profile?.profile_image ? (
            <img src={profile.profile_image} alt="profile" style={ms.avatar} />
          ) : (
            <div style={ms.avatarFallback}>
              <span style={ms.avatarInitials}>{initials}</span>
            </div>
          )}
          <div style={ms.nickName}>{nick}</div>
          <div style={ms.metaRow}>
            {genderChip  && <span style={ms.metaChip}>{genderChip}</span>}
            {profile?.region && <span style={ms.metaChip}>📍 {profile.region}</span>}
          </div>
          {profile?.awards && <div style={ms.awards}>🏆 {profile.awards}</div>}
        </div>

        {/* 선수 정보 */}
        <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 10, letterSpacing: "0.02em" }}>선수 정보</div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div style={ms.spinner} />
          </div>
        ) : !sp ? (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>등록된 종목 프로필이 없어요</div>
        ) : (
          <div style={{ background: "#F5F5F7", borderRadius: 10, padding: "0 14px" }}>
            <ModalInfoRow label="종목" value={`${spIcon} ${spLabel}`} />
            {spLevel && <ModalInfoRow label="레벨" value={spLevel} />}
            <ModalInfoRow label="선수 출신" value={sp.is_player ? "선출" : "비선출"} />
            {genderFull && <ModalInfoRow label="성별" value={genderFull} />}
          </div>
        )}
      </div>
    </div>
  );
}

const ms = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 300, padding: "0 16px",
  },
  sheet: {
    background: "#fff", borderRadius: 16,
    width: "100%", maxWidth: 340,
    padding: "24px 20px",
    position: "relative",
    maxHeight: "80vh", overflowY: "auto",
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  closeBtn: {
    position: "absolute", top: 16, right: 16,
    background: "none", border: "none",
    fontSize: 18, color: "#6b7280", cursor: "pointer",
    lineHeight: 1,
  },
  profileTop: {
    display: "flex", flexDirection: "column", alignItems: "center",
    paddingBottom: 20, borderBottom: "1px solid #f3f4f6", marginBottom: 16,
  },
  avatar: {
    width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginBottom: 12,
  },
  avatarFallback: {
    width: 72, height: 72, borderRadius: "50%", background: "#E8412A",
    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12,
  },
  avatarInitials: { fontSize: 28, fontWeight: 800, color: "#fff" },
  nickName: { fontSize: 20, fontWeight: 800, color: "#1A1A2E", marginBottom: 8 },
  metaRow: { display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginBottom: 4 },
  metaChip: {
    padding: "3px 10px", background: "#F5F5F7", borderRadius: 999,
    fontSize: 12, color: "#374151", fontWeight: 500,
  },
  awards: { fontSize: 12, color: "#d97706", marginTop: 6 },
  spinner: {
    width: 24, height: 24,
    border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A",
    borderRadius: "50%", animation: "spin 0.8s linear infinite",
  },
};

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const s = {
  container: {
    minHeight: "100vh", background: "#f5f6fa",
    maxWidth: 430, margin: "0 auto",
    fontFamily: "'Noto Sans KR', sans-serif",
  },
  header: {
    padding: "calc(env(safe-area-inset-top) + 16px) 20px 16px", background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky", top: 0, zIndex: 10,
  },
  backBtn: {
    display: "flex", alignItems: "center", gap: 4,
    background: "none", border: "none",
    fontSize: 14, fontWeight: 600, color: "#E24B4A",
    cursor: "pointer", padding: 0,
  },
  body: {
    padding: "16px 20px 32px",
  },
  loadingWrap: {
    flex: 1, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "80px 20px", textAlign: "center",
  },
  spinner: {
    width: 32, height: 32,
    border: "3px solid #e5e7eb", borderTop: "3px solid #E24B4A",
    borderRadius: "50%", animation: "spin 0.8s linear infinite",
  },
  card: {
    background: "#fff", borderRadius: 14,
    border: "1px solid #e5e7eb", padding: "20px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  btnGroup: {
    display: "flex", flexDirection: "column", gap: 10, marginTop: 20,
  },
  smallApplyBtn: {
    padding: "4px 12px",
    background: "#fff", color: "#6b7280",
    border: "1px solid #9ca3af", borderRadius: 999,
    fontSize: 12, fontWeight: 500,
    cursor: "pointer", fontFamily: "inherit",
  },
  smallPendingText: {
    fontSize: 12, color: "#9ca3af", fontWeight: 500,
  },
  outlineBtn: {
    width: "100%", padding: "14px",
    background: "#fff", color: "#E24B4A",
    border: "1.5px solid #E24B4A", borderRadius: 12,
    fontSize: 15, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
  },
  dangerOutlineBtn: {
    flex: 1, padding: "11px 8px",
    background: "#fff", color: "#6b7280",
    border: "1px solid #e5e7eb", borderRadius: 10,
    fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "inherit",
  },
  viewerBackdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 500, cursor: "pointer",
  },
  viewerImg: {
    maxWidth: "90%", maxHeight: "85vh", objectFit: "contain", borderRadius: 8,
  },
};
